import { FetchResult } from '@apollo/client';
import { PARTNER_ACCOUNT_SOURCE } from '@stores/user-store';
import { SuspensionOperationType } from 'src/common/__generated__/api/types';
import { SITE_RESOLUTION_LOCALE_MAP } from 'src/constants';

export const isObjectEmpty = (obj: Record<string, unknown>): boolean => {
  return Object.values(obj).every((x) => x === null || x === '');
};

export const getPlatformLangIdFromLocale = (langId: string): number => {
  return SITE_RESOLUTION_LOCALE_MAP[langId] || 1033; // Default to en_US
};

export const getCookie = (name) => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookies = document.cookie.split('; ');
  const cookie = cookies.find((row) => row.startsWith(`${name}=`));
  return cookie ? cookie.split('=')[1] : undefined;
};

export const validateSuspensionNote = (
  note: string,
  validation?: { maxLength?: number; errorMessage?: string },
): { isValid: boolean; errorMessage?: string } => {
  const maxLength = validation?.maxLength || 100;
  const errorMessage = validation?.errorMessage || '';

  if (note.length > maxLength) {
    return {
      isValid: false,
      errorMessage,
    };
  }

  return { isValid: true };
};

export interface SuspensionOperationSuccessResponse {
  __typename: 'SuspensionOperationSuccessResponse';
  heading: string;
  description: string;
}

export interface SuspensionOperationErrorResponse {
  __typename: 'SuspensionOperationErrorResponse';
  heading: string;
  description: string;
}

export type SuspensionOperationResult = SuspensionOperationSuccessResponse | SuspensionOperationErrorResponse;

export const isSuccessResponse = (
  response: SuspensionOperationResult | null,
): response is SuspensionOperationSuccessResponse => {
  return response?.__typename === 'SuspensionOperationSuccessResponse';
};

export const isErrorResponse = (
  response: SuspensionOperationResult | null,
): response is SuspensionOperationErrorResponse => {
  return response?.__typename === 'SuspensionOperationErrorResponse';
};

export const getSuccessResponse = (
  response: SuspensionOperationResult | null,
): SuspensionOperationSuccessResponse | null => {
  return isSuccessResponse(response) ? response : null;
};

export const getErrorResponse = (
  response: SuspensionOperationResult | null,
): SuspensionOperationErrorResponse | null => {
  return isErrorResponse(response) ? response : null;
};

export function resolvePartnerAccountSource(input: unknown): PARTNER_ACCOUNT_SOURCE {
  if (!input) {
    return PARTNER_ACCOUNT_SOURCE.CAP;
  }

  const normalized = String(input).toUpperCase();

  if (Object.values(PARTNER_ACCOUNT_SOURCE).includes(normalized as PARTNER_ACCOUNT_SOURCE)) {
    return normalized as PARTNER_ACCOUNT_SOURCE;
  }

  return PARTNER_ACCOUNT_SOURCE.CAP;
}

export const handleSuspensionResponse = (
  response: FetchResult,
  operationType: SuspensionOperationType.CREATE | SuspensionOperationType.UPDATE | SuspensionOperationType.REMOVE,
  callbacks: {
    setCreateMutationResponse: (response: SuspensionOperationResult | null) => void;
    setUpdateMutationResponse: (response: SuspensionOperationResult | null) => void;
    setRemoveMutationResponse: (response: SuspensionOperationResult | null) => void;
    setShowToast: (show: boolean) => void;
    setIsSheetVisible: (visible: boolean) => void;
    setNoteValue: (value: string) => void;
  },
) => {
  const suspensionResult = response.data?.performSuspensionOperation as SuspensionOperationResult | undefined;

  if (response.errors || isErrorResponse(suspensionResult)) {
    if (operationType === SuspensionOperationType.CREATE) {
      callbacks.setCreateMutationResponse(suspensionResult || null);
    } else if (operationType === SuspensionOperationType.UPDATE) {
      callbacks.setUpdateMutationResponse(suspensionResult || null);
    } else {
      callbacks.setRemoveMutationResponse(suspensionResult || null);
    }
    console.error('Error in suspend operation', response.errors, getErrorResponse(suspensionResult)?.heading);
  } else {
    if (response.data && suspensionResult) {
      if (operationType === SuspensionOperationType.CREATE) {
        callbacks.setCreateMutationResponse(suspensionResult);
      } else if (operationType === SuspensionOperationType.UPDATE) {
        callbacks.setUpdateMutationResponse(suspensionResult);
        if (isSuccessResponse(suspensionResult)) {
          callbacks.setShowToast(true);
          callbacks.setIsSheetVisible(false);
          callbacks.setNoteValue('');
        }
      } else {
        callbacks.setRemoveMutationResponse(suspensionResult);
      }
    }
  }
};

export const handleSuspensionNoteWithValidation = (
  value: string,
  validation: { maxLength?: number; errorMessage?: string } | undefined,
  callbacks: {
    setNoteValue: (value: string) => void;
    setNoteError: (error: string | null) => void;
  },
) => {
  callbacks.setNoteValue(value);
  callbacks.setNoteError(null);

  const validationResult = validateSuspensionNote(value, {
    maxLength: validation?.maxLength,
    errorMessage: validation?.errorMessage,
  });

  if (!validationResult.isValid && validationResult.errorMessage) {
    callbacks.setNoteError(validationResult.errorMessage);
  }
};

export const resetSuspensionSheetState = (callbacks: {
  setIsSheetVisible: (visible: boolean) => void;
  setNoteValue: (value: string) => void;
  setNoteError: (error: string | null) => void;
  setUpdateMutationResponse: (response: SuspensionOperationResult | null) => void;
  setRemoveMutationResponse: (response: SuspensionOperationResult | null) => void;
  setCurrentOperationType: (
    type: SuspensionOperationType.CREATE | SuspensionOperationType.UPDATE | SuspensionOperationType.REMOVE | null,
  ) => void;
}) => {
  callbacks.setIsSheetVisible(false);
  callbacks.setNoteValue('');
  callbacks.setNoteError(null);
  callbacks.setUpdateMutationResponse(null);
  callbacks.setRemoveMutationResponse(null);
  callbacks.setCurrentOperationType(null);
};
